import { AfterContentInit, ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { StepperService } from '../../common-components/stepper/stepper.service';
import {
  asyncScheduler,
  catchError,
  filter,
  Observable,
  observeOn,
  Subject,
  take,
  takeUntil,
  tap,
  throwError
} from 'rxjs';
import { StepperStep } from '../../common-components/stepper/stepper.component';
import { FormComponent, GeneralFormData, OptionsFormData, StepsFormData } from './form-component.interface';
import { ModalButton } from '../../common-components/modal/modal.component';
import { MapConfigData } from './map-editor/map-editor.component';
import {
  CalendarDate,
  GeoCoords,
  ImgInfoBody,
  isStartZoneCircle,
  isTypeDiscovery,
  isTypeMarkerFind,
  isTypeMarkerReach,
  QuestAvailabilityTypes,
  QuestGameAccess,
  QuestMetadata,
  QuestMode,
  QuestsCrudService, QuestStepMetadata,
  QuestStepObjectiveType,
  User
} from 'quest-atlas-angular-components';
import { covertToQuestDropdownValue } from '../../common-components/dropdown/dropdown.component';
import {
  getDifficultyOptions,
  getObjectiveTypeOptions,
  getPrivacyOptions,
  getQuestModeOptions,
  getRecommendedForOptions
} from './dropdown-options-for-forms';
import { GlobalSpinnerService } from '../../services/global-spinner.service';
import { lengthToDegrees } from '@turf/helpers';
import { MAP_EDITOR_URL_FRAGMENT } from '../../constants/nav.constants';
import { TranslocoService } from '@ngneat/transloco';
import { IDB_NAME, IDB_VERSION } from './quest-editor.constants';
import { UserService } from '../../services/user.service';
import { DomSanitizer } from '@angular/platform-browser';
import { idbGeneralFormStore, idbOptionsFormStore, idbStepsFormStore } from './quest-editor.functions';
import { MapConfigDataV2 } from './overall-map-editor/overall-map-editor.component';
import { OptionsFormComponent } from './options-form/options-form.component';

@Component({
    selector: 'app-quest-editor',
    templateUrl: './quest-editor.component.html',
    styleUrls: ['./quest-editor.component.scss'],
    providers: [StepperService],
    standalone: false
})
export class QuestEditorComponent implements OnInit, AfterContentInit, OnDestroy {
  title = this.transloco.translate('editQuest');
  isNew = false;
  originalQuest?: QuestMetadata;
  questId: number;
  adaptive = false;

  steps = [
    {name: 'General', label: this.transloco.translate('qeSteps.general')},
    {name: 'Steps', label: this.transloco.translate('qeSteps.steps')},
    {name: 'Options', label: this.transloco.translate('qeSteps.options')},
    {name: 'Publishing', label: this.transloco.translate('qeSteps.publishing')}
  ];

  generalFormValues: GeneralFormData = {};
  optionsFormValues: OptionsFormData = {};
  stepsFormValues: StepsFormData = {};

  showCloseConfirmation = false;
  showMapEditor = false;
  showMapEditorV2 = false;
  mapEditorConfig?: MapConfigData;
  mapEditorConfigV2?: MapConfigDataV2;
  mapEditorOptions = {
    mapForStepIndex: 0,
    objectiveType: QuestStepObjectiveType.markerFind
  };

  modalButtons: ModalButton[] = [
    {
      type: 'secondary',
      text: this.transloco.translate('stay'),
      onClick: () => (this.showCloseConfirmation = false)
    },
    {
      type: 'error',
      text: this.transloco.translate('leave'),
      onClick: () => this.close()
    }
  ];

  userGeo?: GeoCoords;
  questToSave: QuestMetadata;

  currentStep$: Observable<StepperStep> = this.stepperService.onStepChange$();

  private _generalFormStep!: FormComponent;
  private _optionsFormStep!: FormComponent;
  private _stepsFormStep!: FormComponent;
  private _publishingView: any;
  private db: IDBDatabase;

  currentStep?: FormComponent;
  user: User;

  get generalFormStep(): FormComponent {
    return this._generalFormStep;
  }

  @ViewChild('generalForm')
  set generalFormStep(value: FormComponent) {
    this._generalFormStep = value;

    if (value) {
      this.currentStep = value;
      this.cdr.detectChanges();
    }
  }

  get optionsFormStep(): FormComponent {
    return this._optionsFormStep;
  }

  @ViewChild('optionsForm')
  set optionsFormStep(value: FormComponent) {
    this._optionsFormStep = value;
    if (value) {
      this.currentStep = value;
      this.cdr.detectChanges();
    }
  }

  get stepsFormStep(): FormComponent {
    return this._stepsFormStep;
  }

  @ViewChild('stepsForm')
  set stepsFormStep(value: FormComponent) {
    this._stepsFormStep = value;
    if (value) {
      this.currentStep = value;
      this.cdr.detectChanges();
    }
  }

  get publishingView(): any {
    return this._publishingView;
  }

  @ViewChild('publishingView')
  set publishingView(value: any) {
    if (!value) {
      return;
    }
    this._publishingView = value;
    this.currentStep = undefined;
    this.cdr.detectChanges();
  }

  destroy$ = new Subject<void>();

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private stepperService: StepperService,
    private cdr: ChangeDetectorRef,
    private questsCrudService: QuestsCrudService,
    private globalSpinnerService: GlobalSpinnerService,
    private userService: UserService,
    private transloco: TranslocoService,
    private ds: DomSanitizer
  ) {
    this.activatedRoute.data.pipe(takeUntil(this.destroy$)).subscribe((data) => {
      this.adaptive = !!data['adaptive'];
    });
  }

  ngOnInit(): void {
    // TODO якщо хтось поміняв урлі з адаптів на просту (чи навпаки), то видати помилку, бо ми на квесті з беку будемо бачити, чи він адаптивний, чи ні

    this.userService.getUserInfo$().pipe(takeUntil(this.destroy$)).subscribe((user) => {
      this.user = user;
    });

    const request = window.indexedDB.open(IDB_NAME, IDB_VERSION);
    request.onsuccess = (event) => {
      this.db = (event.target as any).result;
    };

    request.onupgradeneeded = (event) => {
      this.db = (event.target as any).result;

      if (!this.db.objectStoreNames.contains('quest.generalForm')) {
        this.db.createObjectStore('quest.generalForm', {autoIncrement: true});
      }
      if (!this.db.objectStoreNames.contains('quest.stepsForm')) {
        this.db.createObjectStore('quest.stepsForm', {autoIncrement: true});
      }
      if (!this.db.objectStoreNames.contains('quest.optionsForm')) {
        this.db.createObjectStore('quest.optionsForm', {autoIncrement: true});
      }
      if (!this.db.objectStoreNames.contains('quest.adaptive.generalForm')) {
        this.db.createObjectStore('quest.adaptive.generalForm', {autoIncrement: true});
      }
      if (!this.db.objectStoreNames.contains('quest.adaptive.stepsForm')) {
        this.db.createObjectStore('quest.adaptive.stepsForm', {autoIncrement: true});
      }
      if (!this.db.objectStoreNames.contains('quest.adaptive.optionsForm')) {
        this.db.createObjectStore('quest.adaptive.optionsForm', {autoIncrement: true});
      }
    };

    navigator.geolocation.getCurrentPosition((value) => {
      this.userGeo = {lng: value.coords.longitude, lat: value.coords.latitude};
    });

    this.activatedRoute.params.subscribe((value) => {
      if (value['id'] === 'new') {
        this.title = this.transloco.translate('newQuest');
        this.isNew = true;
      } else {
        this.globalSpinnerService.showSpinner();
        this.questsCrudService
          .loadQuestById(value['id'], true)
          .pipe(takeUntil(this.destroy$))
          .subscribe((quest) => {
            this.globalSpinnerService.hideSpinner();

            this.questId = quest.id;
            this.originalQuest = JSON.parse(JSON.stringify(quest));

            this.generalFormValues = {
              name: quest.name,
              // mode: covertToQuestDropdownValue(
              //   quest.competitiveMode ? QuestMode.competitive : quest.teamMode ? QuestMode.group : QuestMode.single,
              //   getQuestTypeOptions()
              // ),
              mode: covertToQuestDropdownValue(quest.config?.mode ?? QuestMode.standard, getQuestModeOptions()),
              recommendedFor: covertToQuestDropdownValue(quest.info.recommendedFor, getRecommendedForOptions()),
              privacy: covertToQuestDropdownValue(quest.info.public, getPrivacyOptions())
            };

            if (quest.organization) {
              this.generalFormValues.organizationId = {
                value: quest.organization.id,
                title: quest.organization.name
              }

              if (quest.organization.secondaryLogo.startsWith('http')) {
                this.generalFormValues.organizationId['imgUrl'] = quest.organization.secondaryLogo;
              } else {
                this.generalFormValues.organizationId['imgSvg'] = this.ds.bypassSecurityTrustHtml(quest.organization.secondaryLogo);
              }
            }

            let levelRequired = 1;
            let onlyOneCompletion = false;

            if (quest.gameAccess) {
              quest.gameAccess.forEach((access) => {
                if (access.accessType === 'level') {
                  levelRequired = access.config['level'];
                }
              });

              onlyOneCompletion = !!quest.gameAccess.find(
                (access) => access.accessType === 'onlyOneCompletion'
              )?.config?.['isSet'];
            }

            this.optionsFormValues = {
              difficulty: covertToQuestDropdownValue(
                quest.info.difficulty,
                getDifficultyOptions()
              ),
              uploadedImages: quest.info.images,
              levelRequired,
              onlyOneCompletion,
              stepsHidden: quest.config.stepsHidden,
              tags: quest.info.tags,
              fromDate: quest.info.availability?.data?.fromDate ? new Date(quest.info.availability?.data?.fromDate.year, quest.info.availability?.data?.fromDate.month, quest.info.availability?.data?.fromDate.day) : undefined,
              toDate: quest.info.availability?.data?.toDate ? new Date(quest.info.availability?.data?.toDate.year, quest.info.availability?.data?.toDate.month, quest.info.availability?.data?.toDate.day) : undefined,
              approxDistance: quest.info.approxDistance,
              approxTime: quest.info.approxTime,
              description: quest.description?.at(0)?.data,
              address: quest.info.address,
              pathways: quest.config.pathways,
              comments: quest.config.comments,
              keywords: quest.keywords
            };

            this.stepsFormValues = {
              steps: quest.steps.map((qs) => {
                return {
                  id: qs.id,
                  name: qs.name,
                  objectives: qs.functionalMetadata.objectives.map(obj => {
                    return {
                      type: covertToQuestDropdownValue(obj.type, getObjectiveTypeOptions()),
                      data: obj
                    }
                  }),
                  task: qs.functionalMetadata.clueMetadata?.at(0)?.data,
                  finalDescription: qs.finalDescription,
                  timer: qs.functionalMetadata.timer,
                  hazardZones: qs.functionalMetadata.hazardZones,
                  startZone: qs.functionalMetadata.startZone,
                  taskImages: qs.functionalMetadata.images,
                  canBeSkipped: qs.functionalMetadata.canBeSkipped,
                  encounter: qs.functionalMetadata.encounter,
                  precondition: qs.functionalMetadata.precondition
                };
              })
            };
          });
      }
    });

    this.activatedRoute.fragment.pipe(filter(v => v !== MAP_EDITOR_URL_FRAGMENT), takeUntil(this.destroy$))
      .subscribe((value) => {
        this.showMapEditor = false;
      });

    // we need to wait for the last form updates to be applied
    this.currentStep$.pipe(observeOn(asyncScheduler, 50), takeUntil(this.destroy$)).subscribe((value) => {
      if (value?.name.toLowerCase() === 'publishing') {
        this.questToSave = this.formsToQuestMetadataBody();
      }
    });
  }

  ngAfterContentInit(): void {
  }

  clickNext() {
    this.stepperService.currentStep$.pipe(take(1)).subscribe((value) => {
      if (!value.completed) {
        this.stepperService.completeCurrentStep();
      }

      this.stepperService.goToNextStep();

      const formValue = this.currentStep?.submitForm();

      this.updateForms(formValue);
    });
  }

  clickBack() {
    if (this.currentStep) {
      this.updateForms(this.currentStep.getFormData());
    }

    this.stepperService.goToPrevStep();
  }

  askToClose(): void {
    this.showCloseConfirmation = true;
  }

  close(): void {
    this.router.navigate(['/home']);
  }

  saveQuest(draft = false): void {
    let quest = this.formsToQuestMetadataBody();
    quest.isDraft = draft;
    quest.adaptive = this.adaptive;

    if (!this.isNew) {
      quest.id = this.questId;
    }

    const firstStartZone = quest.steps[0].functionalMetadata.startZone;

    if (firstStartZone) {
      if (isStartZoneCircle(firstStartZone)) {
        quest.locationPoint = firstStartZone.coords;
      } else {
        quest.locationPoint = firstStartZone.vertices[firstStartZone.connections[0].startVertexId].coords;
      }
    } else {
      const firstStep = quest.steps[0];
      if (isTypeMarkerReach(firstStep.functionalMetadata.objectives[0])) {
        quest.locationPoint = firstStep.functionalMetadata.objectives[0].marker.coords;
      } else if (isTypeMarkerFind(firstStep.functionalMetadata.objectives[0])) {
        //we can't just put as a quest location the location of a marker to find. So, we have to randomise the location a bit
        const coords: GeoCoords = {...firstStep.functionalMetadata.objectives[0].marker.data.circleCenter};
        const a = lengthToDegrees(firstStep.functionalMetadata.objectives[0].marker.data.radius, 'meters');
        //500 is for "not closer than 500m" basically
        const b = lengthToDegrees(firstStep.functionalMetadata.objectives[0].marker.data.radius / 3, 'meters');

        const lngDelta = (Math.random() - 0.5) * 2 * a;
        const latDelta = (Math.random() - 0.5) * 2 * a;
        coords.lng += lngDelta + (lngDelta > 0 ? b : -b);
        coords.lat += latDelta + (latDelta > 0 ? b : -b);
        quest.locationPoint = coords;
      } else if (isTypeDiscovery(firstStep.functionalMetadata.objectives[0])) {
        console.log('firstStep.functionalMetadata.objectives', firstStep.functionalMetadata.objectives);
        quest.locationPoint = firstStep.functionalMetadata.objectives[0].zone.vertices[firstStep.functionalMetadata.objectives[0].zone.connections[0].startVertexId].coords;
      }
    }

    if (quest.config?.mode === QuestMode.noMap) {
      const locationPoints = quest.steps.map(s => this.stepLocationPoint(s));

      const averageLocationPoint: GeoCoords = locationPoints.reduce((acc, loc) => {
        acc.lat += loc.lat;
        acc.lng += loc.lng;
        return acc;
      }, {lat: 0, lng: 0});

      averageLocationPoint.lat /= locationPoints.length;
      averageLocationPoint.lng /= locationPoints.length;

      // randomize quest location between 300m and 1000m in a random direction
      const minMeters = 300;
      const maxMeters = 1000;
      const randomMeters = minMeters + Math.random() * (maxMeters - minMeters);
      const degOffset = lengthToDegrees(randomMeters, 'meters');
      const angle = Math.random() * 2 * Math.PI;
      averageLocationPoint.lat += Math.sin(angle) * degOffset;
      averageLocationPoint.lng += Math.cos(angle) * degOffset;

      quest.locationPoint = averageLocationPoint;

    }

    const obs$ = this.isNew ?
      this.questsCrudService.createQuest(quest).pipe(tap(value => {
        // we can't just replace the quest, because we'll lose images then
        quest.id = value.id;
        for (let i = 0; i < quest.steps.length; i++) {
          quest.steps[i].id = value.steps[i].id;
        }
      }))
      : this.questsCrudService.updateQuest(quest);

    this.globalSpinnerService.showSpinner();
    obs$.pipe(
      catchError((err) => {
        this.globalSpinnerService.hideSpinner();
        console.error(err);

        return throwError(() => err);
      }),
    ).subscribe(() => {
      this.globalSpinnerService.hideSpinner();

      this.updateImages(quest);

      if (this.db) {
        let transaction = this.db.transaction([
          idbGeneralFormStore(this.adaptive),
          idbOptionsFormStore(this.adaptive),
          idbStepsFormStore(this.adaptive)
        ], 'readwrite');
        transaction.objectStore(idbGeneralFormStore(this.adaptive)).clear();
        transaction.objectStore(idbOptionsFormStore(this.adaptive)).clear();
        transaction.objectStore(idbStepsFormStore(this.adaptive)).clear();
      }

      this.router.navigate(['/']);
    });
  }

  private stepLocationPoint(step: QuestStepMetadata): GeoCoords {
    let stepLocationPoint: GeoCoords;

    if (isTypeMarkerReach(step.functionalMetadata.objectives[0])) {
      stepLocationPoint = step.functionalMetadata.objectives[0].marker.coords;
    } else if (isTypeMarkerFind(step.functionalMetadata.objectives[0])) {
      //we can't just put as a quest location the location of a marker to find. So, we have to randomise the location a bit
      const coords: GeoCoords = {...step.functionalMetadata.objectives[0].marker.data.circleCenter};
      const a = lengthToDegrees(step.functionalMetadata.objectives[0].marker.data.radius, 'meters');
      //500 is for "not closer than 500m" basically
      const b = lengthToDegrees(step.functionalMetadata.objectives[0].marker.data.radius / 3, 'meters');

      const lngDelta = (Math.random() - 0.5) * 2 * a;
      const latDelta = (Math.random() - 0.5) * 2 * a;
      coords.lng += lngDelta + (lngDelta > 0 ? b : -b);
      coords.lat += latDelta + (latDelta > 0 ? b : -b);
      stepLocationPoint = coords;
    } else if (isTypeDiscovery(step.functionalMetadata.objectives[0])) {
      stepLocationPoint = step.functionalMetadata.objectives[0].zone.vertices[step.functionalMetadata.objectives[0].zone.connections[0].startVertexId].coords;
    }

    return stepLocationPoint;
  }

  // TODO later may be optimised
  private updateImages(quest: QuestMetadata): void {
    const questImages = quest.info?.images || [];
    const stepsImages: (ImgInfoBody[] | undefined)[] = [];
    quest.info!.images = [];

    for (const step of quest.steps) {
      stepsImages.push(step.functionalMetadata.images || []);
      step.functionalMetadata.images = undefined;
    }

    //No need for takeUntil, because even if the component is destroyed, we want to save images after a quest is saved
    //saving only images never saved before (without url)
    let questImagesToDelete = [];

    if (this.originalQuest) {
      for (const prevImg of this.originalQuest.info.images) {
        let removed = true;
        // iterating to see if the image was removed
        for (const img of questImages) {
          if (img.name === prevImg.name) {
            removed = false;
          }
        }

        if (removed) {
          questImagesToDelete.push(prevImg);
        }
      }
    }

    if (questImages && questImages.length) {
      this.questsCrudService
        .saveImages(
          quest.id,
          questImages.map((image) => {
            if (image.id) {
              return {
                id: image.id,
                description: image.description
              }
            } else {
              return {
                blob: image.raw,
                description: image.description
              }
            }
          }),
          'quest')
        .subscribe();
    }

    for (const image of questImagesToDelete) {
      this.questsCrudService.deleteImage(image.name).subscribe();
    }

    for (let i = 0; i < stepsImages.length; i++) {
      const stepImages = stepsImages[i];
      const stepImagesToDelete = [];

      if (this.originalQuest) {
        if (this.originalQuest.steps.length > i) {
          for (const prevImg of this.originalQuest.steps[i].functionalMetadata.images) {
            let removed = true;
            // iterating to see if the image was removed
            for (const img of stepImages) {
              if (img.name === prevImg.name) {
                removed = false;
              }
            }

            if (removed) {
              stepImagesToDelete.push(prevImg);
            }
          }
        }
      }

      if (stepImages && stepImages.length) {
        this.questsCrudService
          .saveImages(
            quest.steps[i].id,
            stepImages.map((image) => {
              if (image.id) {
                return {
                  id: image.id,
                  description: image.description
                }
              } else {
                return {
                  blob: image.raw,
                  description: image.description
                }
              }
            }),
            'step'
          )
          .subscribe();
      }

      for (const image of stepImagesToDelete) {
        this.questsCrudService.deleteImage(image.name).subscribe();
      }
    }
  }

  formsToQuestMetadataBody(): QuestMetadata {
    const quest: QuestMetadata = {
      name: this.generalFormValues.name,
      organization: this.generalFormValues.organizationId && this.user
        ? this.user.organizations.filter((org) => org.id === this.generalFormValues.organizationId.value)[0]
        : undefined,
      locationPoint: undefined,
      steps: [],
      config: {
        mode: this.generalFormValues.mode?.value || QuestMode.standard,
        // to make sure that the value is boolean
        stepsHidden: !!this.optionsFormValues.stepsHidden,
        pathways: this.optionsFormValues.pathways || [],
        comments: this.optionsFormValues.comments || []
      },
      keywords: this.optionsFormValues.keywords?.toLowerCase(),
      info: {
        images: this.optionsFormValues.uploadedImages,
        difficulty: this.optionsFormValues?.difficulty?.value,
        public: this.generalFormValues.privacy?.value,
        recommendedFor: this.generalFormValues.recommendedFor?.value,
        approxDistance: this.optionsFormValues.approxDistance,
        approxTime: this.optionsFormValues.approxTime,
        address: this.optionsFormValues.address,
        availability: {type: QuestAvailabilityTypes.always},
      },
      gameAccess: []
    };
    if (this.optionsFormValues.description) {
      quest.description = [{type: 'text', data: this.optionsFormValues.description}];
    }
    // switch (this.generalFormValues.mode?.value) {
    //   case QuestMode.single:
    //     quest.teamMode = false;
    //     quest.competitiveMode = false;
    //     break;
    //   case QuestMode.group:
    //     quest.teamMode = true;
    //     quest.competitiveMode = false;
    //     break;
    //   case QuestMode.competitive:
    //     quest.teamMode = true;
    //     quest.competitiveMode = false;
    //     break;
    // }

    if (this.optionsFormValues.levelRequired) {
      const originalLevel = this.originalQuest?.gameAccess?.find(access => access.accessType === 'level');

      const newLevelAccess: QuestGameAccess = {
        accessType: 'level',
        config: {level: this.optionsFormValues.levelRequired}
      };

      if (originalLevel) {
        newLevelAccess.id = originalLevel.id;
      }

      quest.gameAccess.push(newLevelAccess);
    }

    if (this.optionsFormValues.onlyOneCompletion) {
      const originalOnlyOneCompletion = this.originalQuest?.gameAccess?.find(access => access.accessType === 'onlyOneCompletion');

      const newOnlyOneCompletionAccess: QuestGameAccess = {accessType: 'onlyOneCompletion', config: {isSet: true}};

      if (originalOnlyOneCompletion) {
        newOnlyOneCompletionAccess.id = originalOnlyOneCompletion.id;
      }

      quest.gameAccess.push(newOnlyOneCompletionAccess);
    }

    if (this.optionsFormValues.fromDate && this.optionsFormValues.toDate) {
      quest.info!.availability = {
        type: QuestAvailabilityTypes.fromDateToDate,
        data: {
          fromDate: this.convertToCalendarDate(this.optionsFormValues.fromDate),
          toDate: this.convertToCalendarDate(this.optionsFormValues.toDate)
        }
      };
    } else if (this.optionsFormValues.fromDate) {
      quest.info!.availability = {
        type: QuestAvailabilityTypes.fromDate,
        data: {
          fromDate: this.convertToCalendarDate(this.optionsFormValues.fromDate)
        }
      };
    } else if (this.optionsFormValues.toDate) {
      quest.info!.availability = {
        type: QuestAvailabilityTypes.toDate,
        data: {
          toDate: this.convertToCalendarDate(this.optionsFormValues.toDate)
        }
      };
    }

    if (this.optionsFormValues.tags) {
      // doing this to omit sending selected property
      quest.info!.tags = this.optionsFormValues.tags.map((tag) => ({
        id: tag.id,
        name: tag.name,
        description: tag.description
      }));
    }

    for (const step of this.stepsFormValues.steps) {
      quest.steps.push({
        id: step.id,
        name: step.name,
        functionalMetadata: {
          timer: step.timer,
          clueMetadata: [
            {
              type: 'text',
              data: step.task
            }
          ],
          objectives: step.objectives.map(obj => obj.data),
          startZone: step.startZone,
          hazardZones: step.hazardZones,
          images: step.taskImages,
          canBeSkipped: step.canBeSkipped,
          encounter: step.encounter,
          precondition: step.precondition
        },
        finalDescription: step.finalDescription
      });
    }

    return quest;
  }

  openMapEditorForStep(event: any): void {
    this.updateForms(event.form);
    this.showMapEditor = true;

    this.router.navigate([`./`], {relativeTo: this.activatedRoute, fragment: MAP_EDITOR_URL_FRAGMENT});

    const step = event.form.steps[event.stepIndex] as (typeof this.stepsFormValues.steps[0]);

    this.mapEditorOptions.mapForStepIndex = event.stepIndex;

    this.mapEditorConfig = {
      questMode: this.generalFormValues.mode?.value || QuestMode.standard,
      hazardZones: step.hazardZones,
      startZone: step.startZone,
      mapObjectives: (step.objectives || []).map(o => o.data),
      timer: step.timer,
      startZoneHidden: event.stepIndex > 0
    };
  }

  openMapEditorForOptions(event: any) {
    this.updateForms(event.form);
    this.showMapEditorV2 = true;

    this.router.navigate([`./`], {relativeTo: this.activatedRoute, fragment: MAP_EDITOR_URL_FRAGMENT});

    const allMarkersReach = [];
    const allMarkersFind = [];
    const allDiscoveries = [];

    for (let i = 0; i < this.stepsFormValues.steps.length; i++) {
      const step = this.stepsFormValues.steps[i];

      for (const obj of step.objectives) {
        if (obj.data['marker']) {
          allMarkersReach.push({
            step: i,
            vertex: obj.data['marker']
          });
          continue;
        }
        if (obj.data['marker']) {
          allMarkersFind.push({
            step: i,
            vertex: obj.data['marker']
          });
          continue;
        }
        if (obj.data['zone']) {
          allDiscoveries.push({
            step: i,
            graph: obj.data['zone'],
          });
        }
      }
    }

    this.mapEditorConfigV2 = {
      pathways: event.form.pathways || [],
      comments: event.form.comments || [],
      hazardZones: event.form.hazardZones,
      startZone: event.form.startZone,
      allMarkersReach,
      allMarkersFind,
      allDiscoveries,
    };
  }

  onMapApply(event: MapConfigData) {
    this.showMapEditor = false;
    this.router.navigate([`./`], {
      relativeTo: this.activatedRoute,
      fragment: this.stepperService.currentStep$.value.name
    });

    const stepValues = this.stepsFormValues.steps[this.mapEditorOptions.mapForStepIndex];
    stepValues.hazardZones = event.hazardZones;
    stepValues.startZone = event.startZone;
    stepValues.objectives = event.mapObjectives.map(obj => {
      return {
        type: covertToQuestDropdownValue(obj.type, getObjectiveTypeOptions()),
        data: obj
      }
    });

    if (event.timer) {
      stepValues.timer = event.timer;
    }

    console.log('stepValues', stepValues);
  }

  onMarkingsMapApply(event: MapConfigDataV2) {
    console.log(event);
    this.showMapEditorV2 = false;

    this.optionsFormValues.pathways = event.pathways;
    this.optionsFormValues.comments = event.comments;

    (this.optionsFormStep as OptionsFormComponent).externalFormUpdate(this.optionsFormValues);
  }


  private updateForms(formValue: any): void {
    switch (this.currentStep?.type) {
      case 'general':
        this.generalFormValues = formValue;
        break;
      case 'options':
        this.optionsFormValues = formValue;
        break;
      case 'steps':
        this.stepsFormValues = formValue;
    }
  }

  private convertToCalendarDate(d: any): CalendarDate {
    if (!(d instanceof Date)) {
      d = new Date(d);
    }

    return {day: d.getDate(), month: d.getMonth(), year: d.getFullYear()};
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
